import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";

export default function Onboarding() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState(null);
  const [f, setF] = useState({
    first_name: "", country: "", english_variant: "",
    level: "", primary_goal: "", goals: [], situations: [], daily_minutes: 15,
  });
  useEffect(() => { api.get("/catalog").then(({data}) => setCatalog(data)); }, []);
  const setCountry = (code) => {
    const v = { AU: "Australian", CA: "Canadian", UK: "British" }[code];
    setF({ ...f, country: code, english_variant: v });
  };
  const toggle = (key, val) => setF((s) => ({ ...s, [key]: s[key].includes(val) ? s[key].filter(x => x !== val) : [...s[key], val] }));
  const next = () => setStep((s) => Math.min(7, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));
  const submit = async () => {
    try {
      await api.post("/onboarding", f);
      await refresh();
      toast.success("Plan saved!");
      nav("/assessment");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!catalog) return <div className="p-10 text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="mb-8"><div className="text-sm text-slate-500">Step {step} of 7</div>
          <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-emerald-700" style={{width: `${step/7*100}%`}}/></div>
        </div>
        <div className="rle-card p-6 sm:p-8" data-testid={`onboarding-step-${step}`}>
          {step === 1 && (
            <Section title="Where do you live or plan to move?">
              <div className="grid sm:grid-cols-3 gap-3">
                {catalog.countries.map((c) => (
                  <Choice key={c.code} active={f.country === c.code} onClick={() => setCountry(c.code)} testId={`onboarding-country-${c.code.toLowerCase()}`}>
                    <div className="text-3xl">{c.flag}</div><div className="font-semibold mt-2">{c.name}</div>
                  </Choice>
                ))}
              </div>
            </Section>
          )}
          {step === 2 && (
            <Section title="What type of English do you want to practise?">
              <div className="grid sm:grid-cols-3 gap-3">
                {["Australian", "Canadian", "British"].map((v) => (
                  <Choice key={v} active={f.english_variant === v} onClick={() => setF({...f, english_variant: v})} testId={`onboarding-variant-${v.toLowerCase()}`}>
                    <div className="font-semibold">{v} English</div>
                  </Choice>
                ))}
              </div>
            </Section>
          )}
          {step === 3 && (
            <Section title="What is your current English level?" subtitle="Estimated only — this helps us tailor your plan.">
              <div className="grid sm:grid-cols-2 gap-3">
                {catalog.levels.map((l) => (
                  <Choice key={l.code} active={f.level === l.code} onClick={() => setF({...f, level: l.code})} testId={`onboarding-level-${l.code}`}>
                    <div className="font-semibold">{l.label}</div>
                  </Choice>
                ))}
              </div>
            </Section>
          )}
          {step === 4 && (
            <Section title="What is your main reason for learning?" subtitle="Pick one primary goal. You can add more below.">
              <div className="grid sm:grid-cols-2 gap-3">
                {catalog.goals.map((g) => (
                  <Choice key={g.code} active={f.primary_goal === g.code} onClick={() => setF({...f, primary_goal: g.code})} testId={`onboarding-goal-${g.code}`}>
                    <div className="font-medium">{g.label}</div>
                  </Choice>
                ))}
              </div>
              <div className="mt-6">
                <div className="text-sm text-slate-600 mb-2">Additional goals (optional)</div>
                <div className="flex flex-wrap gap-2">
                  {catalog.goals.map((g) => (
                    <button key={g.code} type="button" onClick={() => toggle("goals", g.code)} className={`rle-chip ${f.goals.includes(g.code) ? "!bg-emerald-700 !text-white !border-emerald-700" : ""}`} data-testid={`onboarding-extra-goal-${g.code}`}>{g.label}</button>
                  ))}
                </div>
              </div>
            </Section>
          )}
          {step === 5 && (
            <Section title="What situations do you want to practise?">
              <div className="flex flex-wrap gap-2">
                {catalog.situations.map((s) => (
                  <button key={s} type="button" onClick={() => toggle("situations", s)} className={`rle-chip ${f.situations.includes(s) ? "!bg-emerald-700 !text-white !border-emerald-700" : ""}`} data-testid={`onboarding-situation-${s.replace(/\s+/g,'-').toLowerCase()}`}>{s}</button>
                ))}
              </div>
            </Section>
          )}
          {step === 6 && (
            <Section title="How much time can you practise each day?">
              <div className="grid grid-cols-5 gap-3">
                {catalog.time_options.map((t) => (
                  <Choice key={t} active={f.daily_minutes === t} onClick={() => setF({...f, daily_minutes: t})} testId={`onboarding-time-${t}`}>
                    <div className="font-semibold">{t} min</div>
                  </Choice>
                ))}
              </div>
              <div className="mt-6"><Label>Your first name (optional)</Label><Input value={f.first_name} onChange={(e) => setF({...f, first_name: e.target.value})} data-testid="onboarding-firstname-input"/></div>
            </Section>
          )}
          {step === 7 && (
            <Section title="Your Learning Plan" subtitle="Ready to go. You can change any of these later in your profile.">
              <div className="rle-card p-6 bg-emerald-50 border-emerald-200">
                <ul className="space-y-2 text-slate-800">
                  <li><b>Country:</b> {f.country}</li>
                  <li><b>English variant:</b> {f.english_variant}</li>
                  <li><b>Level:</b> {catalog.levels.find(l => l.code === f.level)?.label || "—"}</li>
                  <li><b>Primary goal:</b> {catalog.goals.find(g => g.code === f.primary_goal)?.label || "—"}</li>
                  <li><b>Situations:</b> {f.situations.length ? f.situations.join(", ") : "—"}</li>
                  <li><b>Time per day:</b> {f.daily_minutes} min</li>
                </ul>
              </div>
            </Section>
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="outline" className="rounded-full" onClick={back} disabled={step === 1} data-testid="onboarding-back-btn"><ArrowLeft size={16} className="mr-1"/>Back</Button>
            {step < 7 ? (
              <Button onClick={next} className="rounded-full bg-emerald-700 hover:bg-emerald-800" disabled={
                (step===1 && !f.country) || (step===2 && !f.english_variant) ||
                (step===3 && !f.level) || (step===4 && !f.primary_goal)
              } data-testid={`onboarding-step-${step}-next-btn`}>Continue <ArrowRight size={16} className="ml-1"/></Button>
            ) : (
              <Button onClick={submit} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="onboarding-finish-btn"><Check size={16} className="mr-1"/>Start my plan</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, subtitle, children }) => (
  <div>
    <h2 className="font-display font-bold text-2xl text-slate-900">{title}</h2>
    {subtitle && <p className="text-slate-600 mt-1">{subtitle}</p>}
    <div className="mt-6">{children}</div>
  </div>
);

const Choice = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`text-left p-4 rounded-2xl border-2 transition-all ${active ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
    {children}
  </button>
);
